<template>
  <b-card no-body class="side-card position-relative" style="height: calc(100vh - 166px); min-height: 420px">
    <div
      ref="messages"
      class="h-100 px-4 pt-3"
      :style="{ overflowY: 'auto', paddingBottom: `${composerHeight + 16}px` }"
    >
      <p v-if="timeline.length === 0" class="text-center text-muted my-4">
        Aún no hay comentarios para mostrar
      </p>

      <template v-for="entry in timeline">
        <div
          v-if="entry.dayLabel"
          :key="`day-${entry.timelineType}-${entry.id}`"
          class="d-flex align-items-center fs-12 font-weight-bold text-muted my-3"
        >
          <div class="flex-grow-1 border-top" />
          <span class="px-3">{{ entry.dayLabel }}</span>
          <div class="flex-grow-1 border-top" />
        </div>

        <DrawingRequestShowChatEvent
          v-if="entry.timelineType === 'event'"
          :key="`event-${entry.id}`"
          :event="entry"
        />

        <div
          v-else
          :key="`comment-${entry.id}`"
          class="d-flex align-items-start mb-3"
          :class="{ 'flex-row-reverse': isOwn(entry) }"
        >
          <span
            class="rounded-circle d-flex align-items-center justify-content-center text-white font-weight-bold fs-12 flex-shrink-0"
            :class="isOwn(entry) ? 'ml-2' : 'mr-2'"
            :style="{ width: '32px', height: '32px', background: isOwn(entry) ? '#c2570f' : '#163D7D' }"
          >
            {{ initials(entry.user) }}
          </span>

          <div class="d-flex flex-column" :class="isOwn(entry) ? 'align-items-end' : 'align-items-start'" style="max-width: 75%; min-width: 0">
            <div class="fs-12 mb-1" :class="{ 'text-right': isOwn(entry) }">
              <span class="font-weight-bold fs-13">{{ entry.user | concatName4 }}</span>
              <span class="text-muted ml-1">{{ formatTime(entry.created_at) }}</span>
            </div>

            <div
              class="px-3 py-2 mw-100"
              :style="{
                background: isOwn(entry) ? '#e7edf7' : '#f4f6f9',
                borderRadius: isOwn(entry) ? '14px 4px 14px 14px' : '4px 14px 14px 14px'
              }"
            >
              <p class="mb-0" style="white-space: pre-line; word-break: break-word; line-height: 1.5">{{ entry.content }}</p>
            </div>

            <b-link
              v-if="attachmentType(entry)"
              class="d-inline-flex align-items-center border bg-white fs-13 px-2 py-1 mt-1 mw-100"
              :class="documentTags[entry.id] && `border-${documentTags[entry.id].variant}`"
              style="border-radius: 10px"
              :title="attachmentName(entry)"
              @click="$emit('open-attachment', entry)"
            >
              <b-spinner v-if="loadingAttachmentId === entry.id" small class="mr-2 flex-shrink-0" />
              <font-awesome-icon
                v-else
                :icon="attachmentType(entry) === 'document' ? ['far', 'file-alt'] : ['far', 'image']"
                class="mr-2 flex-shrink-0"
                fixed-width
              />
              <span class="text-clamp-1">{{ attachmentName(entry) }}</span>
              <template v-if="documentTags[entry.id]">
                <b-badge :variant="documentTags[entry.id].variant" class="ml-2 flex-shrink-0">
                  {{ documentTags[entry.id].label }}
                </b-badge>
                <b-badge variant="dark" class="border ml-1 flex-shrink-0">
                  v{{ documentTags[entry.id].version }}
                </b-badge>
              </template>
            </b-link>
          </div>
        </div>
      </template>
    </div>

    <div class="position-absolute" style="left: 0; right: 0; bottom: 0">
      <div class="chat-composer-fade position-absolute" />
      <div ref="composer" class="position-relative px-3 pb-3 pt-4">
        <slot name="composer" />
      </div>
    </div>
  </b-card>
</template>

<script>
import DrawingRequestShowChatEvent from './DrawingRequestShowChatEvent'
import { buildDocumentTags } from '../../../sharedOptions/drawingRequestOptions'

export default {
  name: 'DrawingRequestShowChat',
  components: { DrawingRequestShowChatEvent },
  props: {
    comments: { type: Array, default: () => [] },
    auditEvents: { type: Array, default: () => [] },
    loadingAttachmentId: { type: Number, default: null }
  },
  data () {
    return {
      composerHeight: 0,
      resizeObserver: null
    }
  },
  computed: {
    currentUserId () {
      const user = this.$store.state.currentUser
      return user ? user.id : null
    },
    documentTags () {
      return buildDocumentTags(this.comments)
    },
    timeline () {
      const comments = this.comments.map(comment => ({ ...comment, timelineType: 'comment' }))
      const events = this.auditEvents.map(event => ({ ...event, timelineType: 'event' }))

      const sorted = [...comments, ...events].sort((a, b) => {
        const diff = new Date(a.created_at) - new Date(b.created_at)
        if (diff !== 0) return diff
        return a.timelineType === 'comment' ? -1 : 1
      })

      let lastDay = null
      return sorted.map((entry) => {
        const day = new Date(entry.created_at).toDateString()
        const dayLabel = day !== lastDay ? this.formatDay(entry.created_at) : null
        lastDay = day
        return { ...entry, dayLabel }
      })
    }
  },
  watch: {
    'timeline.length' () {
      this.scrollToBottom()
    }
  },
  mounted () {
    this.composerHeight = this.$refs.composer.offsetHeight

    this.resizeObserver = new ResizeObserver(() => {
      const wasAtBottom = this.isAtBottom()
      this.composerHeight = this.$refs.composer.offsetHeight
      if (wasAtBottom) this.scrollToBottom()
    })
    this.resizeObserver.observe(this.$refs.composer)

    this.scrollToBottom()
  },
  beforeDestroy () {
    if (this.resizeObserver) this.resizeObserver.disconnect()
  },
  methods: {
    isOwn (comment) {
      return comment.user && comment.user.id === this.currentUserId
    },
    initials (user) {
      if (!user) return '?'
      return `${(user.first_name || '')[0] || ''}${(user.last_name || '')[0] || ''}`.toUpperCase()
    },
    attachmentType (comment) {
      if (comment.document && comment.document.id) return 'document'
      if (comment.photo && comment.photo.id) return 'image'
      return null
    },
    attachmentName (comment) {
      return this.attachmentType(comment) === 'document'
        ? comment.document.name
        : (comment.photo.caption || 'Ver imagen')
    },
    formatTime (date) {
      return new Date(date).toLocaleTimeString('es-CR', { hour: 'numeric', minute: '2-digit' })
    },
    formatDay (date) {
      const day = new Date(date)
      const today = new Date()
      const yesterday = new Date()
      yesterday.setDate(today.getDate() - 1)

      if (day.toDateString() === today.toDateString()) return 'Hoy'
      if (day.toDateString() === yesterday.toDateString()) return 'Ayer'

      const label = day.toLocaleDateString('es-CR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
      return label.charAt(0).toUpperCase() + label.slice(1)
    },
    isAtBottom () {
      const el = this.$refs.messages
      if (!el) return true
      return el.scrollHeight - el.scrollTop - el.clientHeight < 40
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const el = this.$refs.messages
        if (el) el.scrollTop = el.scrollHeight
      })
    }
  }
}
</script>

<style scoped>
.chat-composer-fade {
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  border-radius: 0 0 0.7rem 0.7rem;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0, rgba(255, 255, 255, 0.85) 32px, #ffffff 56px);
  -webkit-backdrop-filter: blur(3px);
  backdrop-filter: blur(3px);
  -webkit-mask-image: linear-gradient(to bottom, transparent 0, #000000 24px);
  mask-image: linear-gradient(to bottom, transparent 0, #000000 24px);
}
</style>
